import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, Calendar, Users, Scissors, BarChart2,
  Settings, Code2, LogOut, Menu, X, ChevronDown, Bell,
  BookOpen, Clock, CalendarOff, MessageSquare,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const NAV = [
  { label: 'Dashboard',    icon: LayoutDashboard, to: '/dashboard' },
  { label: 'Calendar',     icon: Calendar,        to: '/calendar' },
  { label: 'Appointments', icon: Clock,            to: '/appointments' },
  { label: 'Clients',      icon: Users,            to: '/clients' },
  { label: 'Services',     icon: Scissors,         to: '/services' },
  { label: 'Staff',        icon: BookOpen,         to: '/staff' },
  { label: 'Time Off',     icon: CalendarOff,      to: '/time-off' },
  { label: 'Reports',      icon: BarChart2,        to: '/reports' },
  { label: 'Messages',     icon: MessageSquare,    to: '/messages' },
  { label: 'Embed Widget', icon: Code2,            to: '/embed' },
  { label: 'Settings',     icon: Settings,         to: '/settings' },
];

function NavItem({ item, collapsed, onClick }) {
  return (
    <NavLink
      to={item.to}
      onClick={onClick}
      className={({ isActive }) =>
        `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-150 group
         ${isActive
           ? 'bg-primary text-white shadow-sm'
           : 'text-slate-400 hover:text-white hover:bg-white/10'}`
      }
    >
      <item.icon size={18} className="shrink-0" />
      {!collapsed && <span className="truncate">{item.label}</span>}
    </NavLink>
  );
}

export default function AppLayout({ children }) {
  const { user, tenant, logout } = useAuth();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  async function handleLogout() {
    await logout();
    navigate('/login');
  }

  return (
    <div className="flex h-screen bg-surface-50 overflow-hidden">

      {/* ── Mobile backdrop ─────────────────────────────────────── */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-20 bg-black/50 md:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* ── Sidebar ─────────────────────────────────────────────── */}
      <aside
        className={`flex flex-col shrink-0 transition-all duration-200 ease-in-out bg-slate-900
                    fixed inset-y-0 left-0 z-30 md:relative md:translate-x-0
                    ${mobileOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
                    ${collapsed ? 'md:w-16' : 'md:w-56'}
                    w-56`}
      >
        {/* Logo */}
        <div className="flex items-center justify-between px-4 py-5 border-b border-white/10">
          {(!collapsed || mobileOpen) && (
            <div className="flex items-center gap-2">
              <div
                className="w-7 h-7 rounded-lg flex items-center justify-center text-white font-bold text-sm"
                style={{ background: tenant?.primaryColor || '#2E5BBA' }}
              >
                {(tenant?.name || 'B')[0]}
              </div>
              <span className="text-white font-semibold text-sm truncate max-w-[110px]">
                {tenant?.name || 'Booking Platform'}
              </span>
            </div>
          )}
          <button
            onClick={() => { setCollapsed(c => !c); setMobileOpen(false); }}
            className="text-slate-400 hover:text-white transition-colors p-1 rounded hidden md:block"
          >
            {collapsed ? <Menu size={18} /> : <X size={18} />}
          </button>
          <button
            onClick={() => setMobileOpen(false)}
            className="text-slate-400 hover:text-white transition-colors p-1 rounded md:hidden"
          >
            <X size={18} />
          </button>
        </div>

        {/* Nav links */}
        <nav className="flex-1 overflow-y-auto py-4 px-2 space-y-0.5">
          {NAV.map(item => (
            <NavItem key={item.to} item={item} collapsed={collapsed && !mobileOpen}
              onClick={() => setMobileOpen(false)} />
          ))}
        </nav>

        {/* User */}
        <div className="border-t border-white/10 p-3">
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 w-full px-3 py-2 rounded-lg text-sm text-slate-400 hover:text-white hover:bg-white/10 transition-all"
          >
            <LogOut size={16} className="shrink-0" />
            {(!collapsed || mobileOpen) && <span>Log out</span>}
          </button>
        </div>
      </aside>

      {/* ── Main area ───────────────────────────────────────────── */}
      <div className="flex flex-col flex-1 min-w-0 overflow-hidden">

        {/* Top bar */}
        <header className="h-14 bg-white border-b border-surface-border flex items-center justify-between px-4 md:px-6 shrink-0">
          <div className="flex items-center gap-3">
            {/* Mobile hamburger */}
            <button
              onClick={() => setMobileOpen(true)}
              className="md:hidden text-ink-muted hover:text-ink transition-colors p-1 rounded"
            >
              <Menu size={20} />
            </button>
            <h1 className="text-sm font-semibold text-ink-secondary">
              {tenant?.name}
            </h1>
          </div>

          <div className="flex items-center gap-3">
            {/* Notifications */}
            <button className="relative w-8 h-8 rounded-lg flex items-center justify-center text-ink-muted hover:bg-surface-100 transition-colors">
              <Bell size={16} />
            </button>

            {/* User menu */}
            <div className="relative">
              <button
                onClick={() => setUserMenuOpen(o => !o)}
                className="flex items-center gap-2 py-1 pl-1 pr-2 rounded-lg hover:bg-surface-100 transition-colors"
              >
                <div className="w-7 h-7 rounded-full bg-primary flex items-center justify-center text-white text-xs font-semibold">
                  {(user?.name || 'U')[0].toUpperCase()}
                </div>
                <span className="text-sm font-medium text-ink hidden sm:block">
                  {user?.name}
                </span>
                <ChevronDown size={14} className="text-ink-muted" />
              </button>

              {userMenuOpen && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setUserMenuOpen(false)} />
                  <div className="absolute right-0 mt-1 w-48 bg-white border border-surface-border rounded-lg shadow-modal z-20 py-1 text-sm">
                    <div className="px-3 py-2 border-b border-surface-border">
                      <p className="font-medium text-ink truncate">{user?.name}</p>
                      <p className="text-ink-muted text-xs truncate">{user?.email}</p>
                    </div>
                    <button
                      onClick={() => { setUserMenuOpen(false); navigate('/settings'); }}
                      className="w-full text-left px-3 py-2 hover:bg-surface-50 text-ink-secondary"
                    >
                      Settings
                    </button>
                    <button
                      onClick={handleLogout}
                      className="w-full text-left px-3 py-2 hover:bg-surface-50 text-red-600"
                    >
                      Log out
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto overflow-x-hidden">
          <div className="min-w-0 w-full h-full">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}
